import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Metadata, CompositeKey, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  IzzyMetadataEngine,
  APIKeyEntityExtended
} from 'mj_generatedentities';
import { BCSaaSOrganizationAPIKeyEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

// Error messages
const ERROR_NO_CONTACT = 'Unable to create API key: Your user account is not linked to a contact. Please contact support.';

/**
 * Display wrapper for APIKey with UI state
 */
interface APIKeyDisplay {
  apiKey: APIKeyEntityExtended;
  isEditing: boolean;
  editForm: APIKeyEditForm;
}

/**
 * Edit form for APIKey
 */
interface APIKeyEditForm {
  label: string;
  status: string;
  expiresAt: string | null;
}

/**
 * State interface for persisting component state
 */
interface APIKeysComponentState {
  selectedOrganizationId?: string;
  statusFilter?: string;
}

/**
 * Interface for interaction events emitted by the component
 */
interface APIKeysInteraction {
  type: 'generateKey' | 'editKey' | 'revokeKey' | 'deleteKey';
  organizationId?: string;
  keyId?: string;
}

/**
 * Interface for query params
 */
interface APIKeysQueryParams {
  organizationId?: string;
}

/**
 * API Keys Management Dashboard
 *
 * Manages API keys for external access with:
 * - Organization selection
 * - Generate new API keys (shown once, hash stored)
 * - List existing keys with status, expiration, last used
 * - Revoke/Suspend keys
 * - Edit label and expiration
 */
@Component({
  standalone: false,
  selector: 'izzy-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.css']
})
export class APIKeysComponent implements OnInit, OnDestroy, IAdminPanel {

  // ========== Inputs/Outputs ==========
  @Input() initialState: APIKeysComponentState | null = null;
  @Output() stateChange = new EventEmitter<APIKeysComponentState>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();
  @Output() interaction = new EventEmitter<APIKeysInteraction>();

  constructor(
    private location: Location,
    private route: ActivatedRoute
  ) {}

  // ========== Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected apiKeys: APIKeyDisplay[] = [];

  // ========== Selection State ==========
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected statusFilter: string = 'All';
  protected statusOptions: string[] = ['All', 'Active', 'Expired', 'Revoked', 'Suspended'];

  // ========== Generate Key Modal ==========
  protected isGenerating: boolean = false;
  protected generateForm = {
    label: '',
    expiresAt: ''
  };
  protected generatedKey: string | null = null;
  protected showGeneratedKeyModal: boolean = false;
  protected copiedToClipboard: boolean = false;

  // ========== Confirm Dialog ==========
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;
  protected confirmDialogButtonText: string = 'Confirm';
  protected confirmDialogDanger: boolean = false;

  // ========== Cleanup ==========
  private destroy$ = new Subject<void>();
  private _pendingOrgId: string | null = null;

  async ngOnInit(): Promise<void> {
    // Subscribe to query param changes for deep linking
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle query params for deep linking
   */
  private handleQueryParams(params: APIKeysQueryParams): void {
    if (params.organizationId) {
      this._pendingOrgId = params.organizationId;
    }

    if (!this.isLoading && this.organizations.length > 0) {
      this.applyPendingSelections();
    }
  }

  /**
   * Apply pending selections from query params
   */
  private applyPendingSelections(): void {
    if (this._pendingOrgId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrgId);
      if (org && org.ID !== this.selectedOrganization?.ID) {
        this.selectOrganization(org);
      }
      this._pendingOrgId = null;
    }
  }

  /**
   * Update URL query params without full navigation
   */
  private updateQueryParams(): void {
    const params = new URLSearchParams();

    if (this.selectedOrganization) {
      params.set('organizationId', this.selectedOrganization.ID);
    }

    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    const newUrl = queryString ? `${path}?${queryString}` : path;
    this.location.replaceState(newUrl);
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Initialize IzzyMetadataEngine
      await IzzyMetadataEngine.Instance.Config(false);

      // Load organizations from cache
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();

      // Apply pending selections or auto-select defaults
      if (this._pendingOrgId) {
        this.applyPendingSelections();
      } else if (!this.selectedOrganization && this.organizations.length > 0) {
        this.selectOrganization(this.organizations[0]);
      }

      // Emit default status (API keys don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading API keys data:', error);
      this.errorMessage = 'Failed to load data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadAPIKeys(): Promise<void> {
    if (!this.selectedOrganization) {
      this.apiKeys = [];
      return;
    }

    try {
      const rv = new RunView();
      const orgKeysRes = await rv.RunView<{ APIKeyID: string }>({
        EntityName: 'BC: Organization API Keys',
        ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}'`,
        Fields: ['APIKeyID'],
        ResultType: 'simple'
      });
      const apiKeyIds = (orgKeysRes?.Success ? orgKeysRes.Results ?? [] : [])
        .map(r => r.APIKeyID)
        .filter((id): id is string => !!id);
      if (apiKeyIds.length === 0) {
        this.apiKeys = [];
        return;
      }

      const idList = apiKeyIds.map(id => `'${id.replace(/'/g, "''")}'`).join(', ');
      const result = await rv.RunView<APIKeyEntityExtended>({
        EntityName: 'MJ: API Keys',
        ExtraFilter: `ID IN (${idList})`,
        ResultType: 'entity_object'
      });

      if (result.Success) {
        this.apiKeys = result.Results.map(key => ({
          apiKey: key,
          isEditing: false,
          editForm: this.keyToForm(key)
        }));

        // Sort by creation date, newest first
        this.apiKeys.sort((a, b) =>
          new Date(b.apiKey.__mj_CreatedAt).getTime() - new Date(a.apiKey.__mj_CreatedAt).getTime()
        );
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
      this.errorMessage = 'Failed to load API keys.';
    }
  }

  // ========== Organization Selection ==========

  selectOrganization(org: mjBizAppsCommonOrganizationEntity): void {
    this.selectedOrganization = org;
    this.apiKeys = [];
    this.loadAPIKeys();
    this.updateQueryParams();
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  // ========== Filtered Keys ==========

  get filteredKeys(): APIKeyDisplay[] {
    if (this.statusFilter === 'All') {
      return this.apiKeys;
    }
    return this.apiKeys.filter(k => k.apiKey.Status === this.statusFilter);
  }

  // ========== Generate Key ==========

  startGenerate(): void {
    this.isGenerating = true;
    this.generateForm = { label: '', expiresAt: '' };
    this.cancelAllEdits();
  }

  cancelGenerate(): void {
    this.isGenerating = false;
    this.generateForm = { label: '', expiresAt: '' };
  }

  async generateKey(): Promise<void> {
    if (!this.selectedOrganization || !this.generateForm.label.trim()) {
      this.errorMessage = 'Please enter a label for the API key.';
      return;
    }

    // Ensure we have a contact ID for the creator
    const currentContactID = IzzyMetadataEngine.Instance.CurrentContactID;
    if (!currentContactID) {
      this.errorMessage = ERROR_NO_CONTACT;
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      // Generate the API key - returns raw key string
      // Server will hash it on save
      const rawKey = APIKeyEntityExtended.GenerateAPIKey('izzy_sk_');

      // Create the API key record
      const md = new Metadata();
      const newKey = await md.GetEntityObject<APIKeyEntityExtended>('MJ: API Keys');

      // Put raw key in Hash field - server-side APIKeyEntityExtendedServer will hash it
      newKey.Hash = rawKey;
      newKey.Label = this.generateForm.label.trim();
      newKey.Status = 'Active';

      if (this.generateForm.expiresAt) {
        newKey.ExpiresAt = new Date(this.generateForm.expiresAt);
      }

      const saveResult = await newKey.Save();
      if (!saveResult) {
        throw new Error('Failed to save API key');
      }

      // Create the bridge row linking the key to the organization
      const bridge = await md.GetEntityObject<BCSaaSOrganizationAPIKeyEntity>('BC: Organization API Keys');
      bridge.OrganizationID = this.selectedOrganization.ID;
      bridge.APIKeyID = newKey.ID;
      bridge.CreatedByPersonID = currentContactID;
      const bridgeResult = await bridge.Save();
      if (!bridgeResult) {
        throw new Error('Failed to link API key to organization');
      }

      // Store the generated key to show to user (raw key, not hash)
      this.generatedKey = rawKey;
      this.showGeneratedKeyModal = true;
      this.copiedToClipboard = false;

      // Reload the keys list
      await this.loadAPIKeys();

      this.cancelGenerate();

      this.interaction.emit({
        type: 'generateKey',
        organizationId: this.selectedOrganization.ID,
        keyId: newKey.ID
      });
    } catch (error) {
      console.error('Error generating API key:', error);
      this.errorMessage = 'Failed to generate API key. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Generated Key Modal ==========

  async copyKeyToClipboard(): Promise<void> {
    if (this.generatedKey) {
      try {
        await navigator.clipboard.writeText(this.generatedKey);
        this.copiedToClipboard = true;
        setTimeout(() => {
          this.copiedToClipboard = false;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  }

  closeGeneratedKeyModal(): void {
    this.showGeneratedKeyModal = false;
    this.generatedKey = null;
    this.copiedToClipboard = false;
  }

  // ========== Edit Mode ==========

  startEdit(display: APIKeyDisplay): void {
    this.cancelAllEdits();
    this.cancelGenerate();
    display.isEditing = true;
    display.editForm = this.keyToForm(display.apiKey);
  }

  cancelEdit(display: APIKeyDisplay): void {
    display.isEditing = false;
    display.editForm = this.keyToForm(display.apiKey);
  }

  cancelAllEdits(): void {
    for (const display of this.apiKeys) {
      display.isEditing = false;
      display.editForm = this.keyToForm(display.apiKey);
    }
  }

  async saveEdit(display: APIKeyDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const key = display.apiKey;

      key.Label = display.editForm.label;
      key.Set('Status', display.editForm.status);
      key.ExpiresAt = display.editForm.expiresAt ? new Date(display.editForm.expiresAt) : null;

      const saveResult = await key.Save();
      if (!saveResult) {
        throw new Error('Failed to save API key');
      }

      await this.loadAPIKeys();
      this.showSuccess('API key updated');

      this.interaction.emit({
        type: 'editKey',
        organizationId: this.selectedOrganization?.ID,
        keyId: key.ID
      });
    } catch (error) {
      console.error('Error updating API key:', error);
      this.errorMessage = 'Failed to update API key. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Revoke/Suspend ==========

  confirmRevoke(display: APIKeyDisplay): void {
    this.showConfirmDialog(
      'Revoke API Key',
      `Are you sure you want to revoke "${display.apiKey.Label}"? This action cannot be undone. The key will no longer be usable.`,
      async () => {
        await this.revokeKey(display);
      },
      'Revoke',
      true
    );
  }

  private async revokeKey(display: APIKeyDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      display.apiKey.Revoke();
      const result = await display.apiKey.Save();
      if (!result) {
        throw new Error('Failed to revoke API key');
      }

      await this.loadAPIKeys();
      this.showSuccess('API key revoked');

      this.interaction.emit({
        type: 'revokeKey',
        organizationId: this.selectedOrganization?.ID,
        keyId: display.apiKey.ID
      });
    } catch (error) {
      console.error('Error revoking API key:', error);
      this.errorMessage = 'Failed to revoke API key. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  async toggleSuspend(display: APIKeyDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const currentStatus = display.apiKey.Get('Status');
      display.apiKey.Set('Status', currentStatus === 'Suspended' ? 'Active' : 'Suspended');

      const result = await display.apiKey.Save();
      if (!result) {
        throw new Error('Failed to update API key status');
      }

      await this.loadAPIKeys();
      this.showSuccess(`API key ${display.apiKey.Get('Status') === 'Suspended' ? 'suspended' : 'activated'}`);
    } catch (error) {
      console.error('Error updating API key status:', error);
      this.errorMessage = 'Failed to update API key status. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Delete ==========

  confirmDelete(display: APIKeyDisplay): void {
    this.showConfirmDialog(
      'Delete API Key',
      `Are you sure you want to permanently delete "${display.apiKey.Label}"?`,
      async () => {
        await this.deleteKey(display);
      },
      'Delete',
      true
    );
  }

  private async deleteKey(display: APIKeyDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const result = await display.apiKey.Delete();
      if (!result) {
        throw new Error('Failed to delete API key');
      }

      await this.loadAPIKeys();
      this.showSuccess('API key deleted');

      this.interaction.emit({
        type: 'deleteKey',
        organizationId: this.selectedOrganization?.ID,
        keyId: display.apiKey.ID
      });
    } catch (error) {
      console.error('Error deleting API key:', error);
      this.errorMessage = 'Failed to delete API key. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Helpers ==========

  private keyToForm(key: APIKeyEntityExtended): APIKeyEditForm {
    return {
      label: key.Label,
      status: key.Get('Status'),
      expiresAt: key.ExpiresAt ? this.formatDateForInput(key.ExpiresAt) : null
    };
  }

  private formatDateForInput(date: Date): string {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(date: Date | null | undefined): string {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Expired': return 'status-expired';
      case 'Revoked': return 'status-revoked';
      case 'Suspended': return 'status-suspended';
      default: return '';
    }
  }

  isExpired(key: APIKeyEntityExtended): boolean {
    if (!key.ExpiresAt) return false;
    return new Date(key.ExpiresAt) < new Date();
  }

  getMaskedHash(hash: string): string {
    if (!hash || hash.length < 12) return hash;
    return `${hash.substring(0, 8)}...${hash.substring(hash.length - 4)}`;
  }

  // ========== Confirm Dialog ==========

  private showConfirmDialog(
    title: string,
    message: string,
    action: () => Promise<void>,
    buttonText: string = 'Confirm',
    danger: boolean = false
  ): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogButtonText = buttonText;
    this.confirmDialogDanger = danger;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  // ========== Utilities ==========

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  openKeyRecord(display: APIKeyDisplay): void {
    this.openRecord.emit({
      EntityName: 'MJ: API Keys',
      RecordPKey: CompositeKey.FromID(display.apiKey.ID)
    });
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadAPIKeysComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
